import {
	autoUpdate,
	flip,
	offset,
	platform,
	shift,
	size,
	useFloating,
	type Middleware,
	type MiddlewareState,
	type Placement,
	type Platform,
	type Rect,
	type ReferenceType,
	type UseFloatingReturn,
} from '@floating-ui/react';
import {
	getPublishedKeyboardHeight,
	subscribeToPublishedKeyboardHeight,
} from './useKeyboardBottomInset';

/**
 * PD-14097 — select menus that stay above the native app's keyboard.
 *
 * Floating UI decides where a menu fits by measuring the visual viewport. In
 * the native CRM app the soft keyboard does not shrink that viewport (see
 * `useKeyboardBottomInset`), so a menu under a field "fits" below it and
 * opens straight under the keyboard: in the Delegate As dialog no user could
 * be picked. Mobile Safari is unaffected, because there the visual viewport
 * does shrink and Floating UI already sees the keyboard.
 *
 * The fix teaches Floating UI where the visible area ends: while the native
 * shell reports a keyboard, the clipping boundary every middleware measures
 * against (flip, shift and size alike) stops at the keyboard's top edge.
 * Clipping the boundary, rather than padding it by the keyboard height,
 * matters for a menu that renders inside its own scroll container (the
 * Combobox): inside an editor Sidebar or a Modal that container already
 * ends above the keyboard, and padding would subtract the keyboard a second
 * time and squeeze the menu to nothing. Only the natively published height
 * is used, so desktop and mobile Safari behave exactly as before.
 */

/** Space (px) the menus keep from the window edges and from the keyboard. */
export const MENU_VIEWPORT_PADDING = 8;

/**
 * Smallest height (px) a menu is capped to while the keyboard is up — about
 * two rows. Below this the menu reads as broken, so it is allowed to run a
 * little under the keyboard instead; flip has already picked the roomier side
 * of the field by then, so that only happens when neither side has room.
 */
export const MIN_KEYBOARD_MENU_HEIGHT = 80;

/** A usable keyboard height (px), or 0 for anything else. */
export function normalizeKeyboardHeight(height: number): number {
	return Number.isFinite(height) && height > 0 ? height : 0;
}

/**
 * The clipping rect with its bottom edge pulled up to the top of the
 * keyboard. `viewport` is the viewport rect in the same coordinates. A rect
 * that already ends above the keyboard (a scroll container inside a Sidebar
 * or Modal that lifts itself) is returned unchanged, so nothing is counted
 * twice.
 */
export function clipRectAboveKeyboard(
	clipping: Rect,
	viewport: Rect,
	keyboardHeight: number,
): Rect {
	const height = normalizeKeyboardHeight(keyboardHeight);
	if (height === 0) {
		return clipping;
	}
	const keyboardTop = viewport.y + viewport.height - height;
	const bottom = Math.min(clipping.y + clipping.height, keyboardTop);

	return { ...clipping, height: Math.max(0, bottom - clipping.y) };
}

/**
 * The max height (px) for a menu while the keyboard is up: what is left
 * between the field and the keyboard (or the top of the window, when the
 * menu flipped above the field), never taller than the menu's own cap and
 * never shorter than `MIN_KEYBOARD_MENU_HEIGHT`. `ownMaxHeight` is the
 * caller's normal cap, or NaN when it has none.
 */
export function getKeyboardMenuMaxHeight(
	availableHeight: number,
	ownMaxHeight: number,
): number {
	const available = Number.isFinite(availableHeight)
		? Math.floor(availableHeight)
		: 0;
	const room = Math.max(MIN_KEYBOARD_MENU_HEIGHT, available);

	return Number.isFinite(ownMaxHeight) && ownMaxHeight > 0
		? Math.min(room, ownMaxHeight)
		: room;
}

/**
 * Floating UI's DOM platform with the clipping boundary stopped at the top
 * of the native keyboard. The keyboard height is read when the position is
 * computed, so the boundary is always current. Exported for its tests.
 */
export const keyboardAwareFloatingPlatform: Platform = {
	...platform,
	async getClippingRect(args) {
		// `this` is the per-computation copy of the platform Floating UI builds
		// (it carries the ancestor cache the DOM implementation reads), so the
		// calls below forward it instead of calling through `platform`.
		const clipping = await platform.getClippingRect.call(this, args);
		const keyboardHeight = getPublishedKeyboardHeight();
		if (keyboardHeight === 0) {
			return clipping;
		}
		// The bare viewport in the same coordinate space, to locate the keyboard.
		const viewport = await platform.getClippingRect.call(this, {
			...args,
			boundary: [],
			rootBoundary: 'viewport',
		});

		return clipRectAboveKeyboard(clipping, viewport, keyboardHeight);
	},
};

/**
 * Floating UI's `autoUpdate`, plus a reposition whenever the native keyboard
 * opens, closes or changes height. `autoUpdate` cannot see the keyboard: the
 * viewport does not resize and nothing scrolls. Only open menus register, and
 * they share the one window listener `subscribeToPublishedKeyboardHeight`
 * keeps.
 */
function keyboardAwareAutoUpdate(
	reference: ReferenceType,
	floating: HTMLElement,
	update: () => void,
): () => void {
	const stopAutoUpdate = autoUpdate(reference, floating, update);
	let frame: number | null = null;

	const stopKeyboardUpdates = subscribeToPublishedKeyboardHeight(() => {
		if (frame != null) {
			cancelAnimationFrame(frame);
		}
		// Wait a frame so an overlay that moves with the keyboard (a Modal
		// re-centering above it) has re-rendered and the field has moved
		// before the menu measures it.
		frame = requestAnimationFrame(() => {
			frame = null;
			update();
		});
	});

	return (): void => {
		stopAutoUpdate();
		stopKeyboardUpdates();
		if (frame != null) {
			cancelAnimationFrame(frame);
		}
	};
}

/**
 * Caps the menu at the room left above the keyboard, and only while the
 * keyboard is up; otherwise the inline cap is removed so the menu's own
 * max-height class (`maxHeightClassName`, PD-15466) applies untouched.
 */
function capMenuAboveKeyboard(
	floating: HTMLElement,
	availableHeight: number,
	keyboardHeight: number,
): void {
	floating.style.removeProperty('max-height');
	if (keyboardHeight === 0) {
		return;
	}
	// Read the menu's own cap (its max-height class) with the inline cap
	// removed; a menu without one reports `none`, which parses to NaN.
	const ownMaxHeight = Number.parseFloat(getComputedStyle(floating).maxHeight);
	floating.style.maxHeight = `${String(getKeyboardMenuMaxHeight(availableHeight, ownMaxHeight))}px`;
}

export type KeyboardAwareSizeArgs = MiddlewareState & {
	availableWidth: number;
	availableHeight: number;
	/** The native keyboard height (px); 0 when it is closed or absent. */
	keyboardHeight: number;
};

export interface KeyboardAwareFloatingOptions {
	placement?: Placement;
	/**
	 * Replaces the default height handling (cap above the keyboard only while
	 * it is up) with the caller's own `size` logic, for a menu that also sizes
	 * itself when no keyboard is up (Select's width and height bounds).
	 *
	 * It must depend on its arguments only: Floating UI compares middleware
	 * by value, treats functions with the same source as equal and keeps the
	 * earlier render's, so anything it closes over from a later render may
	 * never be seen.
	 */
	applySize?: (args: KeyboardAwareSizeArgs) => void;
}

/**
 * The select-menu middleware: offset 4px, flip above the field when there is
 * no room below, shift inside the window, and size. Every keyboard-dependent
 * value is read when the position is computed, so the array never needs to
 * change for the keyboard (Floating UI compares middleware by value and
 * keeps an earlier render's functions). Exported for its tests.
 */
export function createKeyboardAwareMenuMiddleware(
	applySize?: (args: KeyboardAwareSizeArgs) => void,
): Array<Middleware> {
	return [
		offset(4),
		// With no keyboard this is flip's default (no padding).
		flip(() => ({
			padding:
				getPublishedKeyboardHeight() > 0
					? { bottom: MENU_VIEWPORT_PADDING }
					: 0,
		})),
		shift({ padding: MENU_VIEWPORT_PADDING }),
		size({
			padding: MENU_VIEWPORT_PADDING,
			apply(state) {
				const keyboardHeight = getPublishedKeyboardHeight();
				if (applySize != null) {
					applySize({ ...state, keyboardHeight });
					return;
				}
				capMenuAboveKeyboard(
					state.elements.floating,
					state.availableHeight,
					keyboardHeight,
				);
			},
		}),
	];
}

/**
 * `useFloating` for a select menu that opens under its field, portaled or
 * not, that also stays above the native app's keyboard and repositions when
 * the keyboard moves. Used by SelectSearch, Select and Combobox so the three
 * cannot drift apart.
 */
export function useKeyboardAwareFloating(
	options: KeyboardAwareFloatingOptions = {},
): UseFloatingReturn {
	const { placement = 'bottom-start', applySize } = options;

	return useFloating({
		placement,
		platform: keyboardAwareFloatingPlatform,
		middleware: createKeyboardAwareMenuMiddleware(applySize),
		whileElementsMounted: keyboardAwareAutoUpdate,
	});
}
